import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FeatureTiers from './FeatureTiers';
import Branding from './Branding';

/**
 * FeatureGate
 *
 * A component that conditionally renders content based on feature access
 * Can show upgrade messages for locked features
 *
 * Modes:
 * - 'block': Traditional upgrade message box (default)
 * - 'inline': iOS-style greyed-out overlay for buttons/controls
 */
const FeatureGate = ({
  feature,
  userTier,
  user,
  fallback,
  showUpgradeMessage = true,
  mode = 'block', // 'block' or 'inline'
  onShowToast, // Callback to show toast notification
  children
}) => {
  // Check if the user has access to this feature
  const hasAccess = FeatureTiers.hasAccess(feature, user);

  // If the user has access, render the children
  if (hasAccess) {
    return children;
  }

  // If fallback is provided, render it
  if (fallback) {
    return fallback;
  }

  // INLINE MODE: iOS/FaceTime style greyed-out overlay for buttons
  if (mode === 'inline' && showUpgradeMessage) {
    const requiredTier = FeatureTiers.FEATURES[feature];
    const isUnlimitedFeature = requiredTier === FeatureTiers.TIERS.UNLIMITED ||
                                requiredTier === FeatureTiers.TIERS.EXPERT ||
                                requiredTier === FeatureTiers.TIERS.PREMIUM;
    const isHomeFeature = requiredTier === FeatureTiers.TIERS.HOME;

    const targetTier = isHomeFeature ? 'home' : 'unlimited';
    const targetUrl = user ?
      `/upgrade?tier=${targetTier}&feature=${feature}` :
      `/login?mode=signup&tier=${targetTier}`;

    const handleUpgradeClick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Show toast notification with Share with Parent button
      if (onShowToast) {
        const tierName = targetTier === 'home' ? 'Home' : 'Unlimited';

        // Kid-friendly benefit-focused messages with clear unlock path
        const featureMessages = {
          'FRIEND_TRAINING_HOST': `🔒 Want to study with friends? Unlock with Apple Pay or share with a parent`,
          'SCREEN_SHARING': `🔒 Want Mira to see your homework for better help? Unlock with Apple Pay or share with a parent`,
          'FRIEND_NETWORK_EFFECTS': `🔒 Want to study with friends? Unlock with Apple Pay or share with a parent`,
          'FRIEND_TRAINING_ANALYTICS': `🔒 Want to track your progress? Unlock with Apple Pay or share with a parent`
        };

        const message = featureMessages[feature] || `🔒 Unlock with Apple Pay or share with a parent`;

        const actionButton = {
          label: 'Unlock Now',
          onClick: () => {
            // Trigger share modal - this will be handled by parent component
            window.dispatchEvent(new CustomEvent('showShareWithParent', {
              detail: { tier: targetTier, feature }
            }));
          }
        };

        onShowToast(message, 'warning', actionButton);
      } else {
        // Fallback to navigation if no toast available
        window.location.href = targetUrl;
      }
    };

    return (
      <div
        style={{
          position: 'relative',
          display: 'inline-block'
        }}
      >
        {/* Wrapped feature (greyed out) */}
        <div
          style={{
            filter: 'grayscale(100%) brightness(0.9)',
            opacity: 0.5,
            pointerEvents: 'none',
            position: 'relative'
          }}
        >
          {children}
        </div>

        {/* Glass morphism overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            borderRadius: 'inherit',
            cursor: 'pointer',
            pointerEvents: 'all'
          }}
          onClick={handleUpgradeClick}
          whileHover={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(4px)'
          }}
          whileTap={{ scale: 0.98 }}
        >
          {/* Lock icon badge - top right corner */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            style={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.1)',
              zIndex: 10
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"
                fill="white"
              />
            </svg>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // BLOCK MODE: If no fallback provided but we want to show an upgrade message
  if (showUpgradeMessage) {
    // Check what tier is required for this feature
    const requiredTier = FeatureTiers.FEATURES[feature];
    
    // Map legacy tier requirements to new simplified tiers
    const isUnlimitedFeature = requiredTier === FeatureTiers.TIERS.EXPERT || 
                               requiredTier === FeatureTiers.TIERS.PREMIUM ||
                               requiredTier === FeatureTiers.TIERS.UNLIMITED;
    const isHomeFeature = requiredTier === FeatureTiers.TIERS.EXPERT_FAMILY ||
                          requiredTier === FeatureTiers.TIERS.HOME;
    
    // Get feature-specific CTA
    const upgradeCTA = FeatureTiers.getUpgradeCTA(feature);
    
    // Custom emoji and styling based on tier
    let featureEmoji = '🔒';
    let borderColor = Branding.colors.slateGray;
    let backgroundColor = Branding.colors.offWhite;
    let buttonColor = Branding.colors.vibrantOrange;
    let buttonParams = 'mode=signup';
    
    // Enhanced styling for premium features
    if (isUnlimitedFeature) {
      featureEmoji = '⭐';
      borderColor = Branding.colors.vibrantOrange;
      backgroundColor = '#FFF8F0'; // Light orange tint
      buttonParams = 'mode=signup&tier=unlimited';
    } else if (isHomeFeature) {
      featureEmoji = '👨‍👩‍👧‍👦';
      borderColor = Branding.colors.darkTeal;
      backgroundColor = '#F0FFF8'; // Light teal tint
      buttonParams = 'mode=signup&tier=home';
    }
    
    // Determine target URL based on user authentication status
    let targetTier = 'unlimited'; // Default upgrade tier
    if (isHomeFeature) targetTier = 'home';
    
    const targetUrl = user ? 
      `/upgrade?tier=${targetTier}` : 
      `/login?${buttonParams}`;
    
    // Calculate days until price increase
    const daysRemaining = FeatureTiers.PRICING.getDaysUntilIncrease();
    const priceIncreaseSoon = daysRemaining > 0 && daysRemaining <= 14;
    
    return (
      <div className="feature-locked" style={{
        border: `2px dashed ${borderColor}`,
        borderRadius: Branding.borders.radius.md,
        padding: Branding.spacing.md,
        margin: Branding.spacing.md,
        backgroundColor: backgroundColor,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Optional corner ribbon for premium features */}
        {isUnlimitedFeature && (
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '-25px',
            backgroundColor: Branding.colors.vibrantOrange,
            color: Branding.colors.pureWhite,
            fontSize: Branding.typography.fontSize.xs,
            fontWeight: Branding.typography.fontWeight.bold,
            padding: '3px 25px',
            transform: 'rotate(45deg)',
            boxShadow: Branding.shadows.sm,
            zIndex: 1
          }}>
            UNLIMITED
          </div>
        )}
        
        {isHomeFeature && (
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '-25px',
            backgroundColor: Branding.colors.darkTeal,
            color: Branding.colors.pureWhite,
            fontSize: Branding.typography.fontSize.xs,
            fontWeight: Branding.typography.fontWeight.bold,
            padding: '3px 25px',
            transform: 'rotate(45deg)',
            boxShadow: Branding.shadows.sm,
            zIndex: 1
          }}>
            HOME PLAN
          </div>
        )}
        
        <div className="feature-locked-icon" style={{
          fontSize: '2rem',
          marginBottom: Branding.spacing.sm
        }}>
          {featureEmoji}
        </div>
        
        <h3 style={{
          color: Branding.colors.darkSlate,
          marginBottom: Branding.spacing.sm,
          fontSize: Branding.typography.fontSize.lg,
          fontWeight: Branding.typography.fontWeight.bold
        }}>
          {feature.replace(/_/g, ' ')} requires an upgrade
        </h3>
        
        <p style={{
          color: Branding.colors.slateGray,
          marginBottom: Branding.spacing.md,
          fontSize: Branding.typography.fontSize.base
        }}>
          {upgradeCTA}
        </p>
        
        {/* Pricing information for premium features */}
        {isUnlimitedFeature && (
          <div style={{
            backgroundColor: Branding.colors.nearlyWhite,
            borderRadius: Branding.borders.radius.sm,
            padding: Branding.spacing.sm,
            marginBottom: Branding.spacing.md,
            fontWeight: Branding.typography.fontWeight.medium
          }}>
            <p style={{ margin: 0, color: Branding.colors.darkSlate }}>
              <strong>Unlimited Plan:</strong> ${FeatureTiers.PRICING.UNLIMITED_ANNUAL}/year or ${FeatureTiers.PRICING.UNLIMITED_MONTHLY}/month
            </p>
          </div>
        )}
        
        {isHomeFeature && (
          <div style={{
            backgroundColor: Branding.colors.nearlyWhite,
            borderRadius: Branding.borders.radius.sm,
            padding: Branding.spacing.sm,
            marginBottom: Branding.spacing.md,
            fontWeight: Branding.typography.fontWeight.medium
          }}>
            <p style={{ margin: 0, color: Branding.colors.darkSlate }}>
              <strong>Home Plan:</strong> ${FeatureTiers.PRICING.HOME_ANNUAL}/year or ${FeatureTiers.PRICING.HOME_MONTHLY}/month
              <br />
              <small>For up to 5 accounts - Save ${FeatureTiers.PRICING.HOME_SAVINGS_VS_INDIVIDUAL}/year vs individual plans</small>
            </p>
          </div>
        )}
        
        {/* Family plan suggestion for individual features */}
        {isUnlimitedFeature && (
          <div style={{
            fontSize: Branding.typography.fontSize.sm,
            color: Branding.colors.slateGray,
            marginBottom: Branding.spacing.md,
            fontStyle: 'italic'
          }}>
            Need this for multiple students? Consider our Home plan: ${FeatureTiers.PRICING.HOME_ANNUAL}/year for up to 5 accounts
          </div>
        )}
        
        <button 
          onClick={() => window.location.href = targetUrl}
          style={{
            backgroundColor: buttonColor,
            color: Branding.colors.pureWhite,
            border: 'none',
            borderRadius: Branding.borders.radius.md,
            padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
            fontSize: Branding.typography.fontSize.base,
            fontWeight: Branding.typography.fontWeight.medium,
            cursor: 'pointer',
            boxShadow: Branding.shadows.sm,
            transition: Branding.transitions.default
          }}
        >
          {isHomeFeature ? 'Get Home Plan' : isUnlimitedFeature ? 'Get Unlimited' : 'Upgrade Now'}
        </button>
      </div>
    );
  }
  
  // If no fallback and no upgrade message, render nothing
  return null;
};

export default FeatureGate;